import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "cxa.token";

const AuthContext = createContext({
  status: "checking",   // "checking" | "anon" | "authed"
  user: null,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stored = localStorage.getItem(TOKEN_KEY);
        if (!stored) {
          if (alive) setStatus("anon");
          return;
        }
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (!alive) return;
        setUser(data);
        setStatus("authed");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("[auth] check failed:", err);
        localStorage.removeItem(TOKEN_KEY);
        if (alive) {
          setUser(null);
          setStatus("anon");
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onUnauth = () => {
      setUser(null);
      setStatus("anon");
    };
    window.addEventListener("cxa:unauthorized", onUnauth);
    return () => window.removeEventListener("cxa:unauthorized", onUnauth);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setStatus("authed");
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus("anon");
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
